import React, { useState } from 'react';
import { Lock, Loader2, Eye, EyeOff, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const [loading, setLoading] = useState(false);
  const [fixingUrls, setFixingUrls] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [form, setForm] = useState({
    current_password: '',
    new_username: '',
    new_password: '',
    confirm_password: ''
  });

  const getAuthHeader = () => {
    const stored = sessionStorage.getItem('adminCredentials');
    if (stored) {
      const { username, password } = JSON.parse(stored);
      return `Basic ${btoa(`${username}:${password}`)}`;
    }
    return '';
  };

  const handleFixUrls = async () => {
    setFixingUrls(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/fix-urls`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeader()
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Fixed ${data.fixed_urls} URL(s). Refresh the page to see changes.`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to fix URLs');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to fix URLs');
    } finally {
      setFixingUrls(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.current_password) {
      toast.error('Please enter your current password');
      return;
    }

    if (form.new_password && form.new_password !== form.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (form.new_password && form.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify({
          current_password: form.current_password,
          new_username: form.new_username || undefined,
          new_password: form.new_password || undefined
        })
      });

      if (response.ok) {
        toast.success('Credentials updated successfully! Please log in again.');
        // Clear stored credentials and redirect to login
        sessionStorage.removeItem('adminCredentials');
        window.location.href = '/admin';
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update credentials');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Settings">
      <div className="max-w-2xl">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Change Login Credentials
            </CardTitle>
            <CardDescription>
              Update your admin username and/or password. You'll need to log in again after making changes.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Current Password - Required */}
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password *</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showPasswords ? 'text' : 'password'}
                    value={form.current_password}
                    onChange={(e) => setForm(prev => ({ ...prev, current_password: e.target.value }))}
                    placeholder="Enter your current password"
                    className="bg-secondary border-border pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <hr className="border-border" />

              {/* New Username - Optional */}
              <div className="space-y-2">
                <Label htmlFor="new_username">New Username (optional)</Label>
                <Input
                  id="new_username"
                  type="text"
                  value={form.new_username}
                  onChange={(e) => setForm(prev => ({ ...prev, new_username: e.target.value }))}
                  placeholder="Leave blank to keep current username"
                  className="bg-secondary border-border"
                />
              </div>

              {/* New Password - Optional */}
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password (optional)</Label>
                <Input
                  id="new_password"
                  type={showPasswords ? 'text' : 'password'}
                  value={form.new_password}
                  onChange={(e) => setForm(prev => ({ ...prev, new_password: e.target.value }))}
                  placeholder="Leave blank to keep current password"
                  className="bg-secondary border-border"
                />
              </div>

              {/* Confirm New Password */}
              {form.new_password && (
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    type={showPasswords ? 'text' : 'password'}
                    value={form.confirm_password}
                    onChange={(e) => setForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                    placeholder="Confirm your new password"
                    className="bg-secondary border-border"
                  />
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full h-11 bg-foreground text-background hover:bg-foreground/90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Credentials'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* URL Fix Tool */}
        <Card className="bg-card border-border mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Fix Media URLs
            </CardTitle>
            <CardDescription>
              If your audio or images aren't loading correctly, this tool will fix all media URLs in the database 
              to use relative paths, ensuring they work regardless of which domain you're using.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Button 
              onClick={handleFixUrls}
              variant="outline"
              className="w-full h-11"
              disabled={fixingUrls}
            >
              {fixingUrls ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Fixing URLs...
                </>
              ) : (
                <>
                  <Wrench className="h-4 w-4 mr-2" />
                  Fix All Media URLs
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
